import { TableCheckboxData } from '@/ui/Table';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo } from 'react';

type UseGroupEditorTableSelectionArgs<TItem> = {
    items: TItem[];
    page: number;
    setPage: Dispatch<SetStateAction<number>>;
    rowsOnPage: number;
    setRowsOnPage: Dispatch<SetStateAction<number>>;
    pickedValues: string[];
    setPickedValues: Dispatch<SetStateAction<string[]>>;
    getItemKey: (item: TItem) => string;
};

const arraysEqual = (left: string[], right: string[]) =>
    left.length === right.length && left.every((value, index) => value === right[index]);

export const useGroupEditorTableSelection = <TItem>({
    items,
    page,
    setPage,
    rowsOnPage,
    setRowsOnPage,
    pickedValues,
    setPickedValues,
    getItemKey,
}: UseGroupEditorTableSelectionArgs<TItem>) => {
    const pickedValuesSet = useMemo(() => new Set(pickedValues), [pickedValues]);
    const totalRows = items.length;

    const pageCount = useMemo(() => {
        if (!totalRows) {
            return 1;
        }

        return Math.ceil(totalRows / rowsOnPage);
    }, [rowsOnPage, totalRows]);

    useEffect(() => {
        if (page > pageCount) {
            setPage(pageCount);
        }
    }, [page, pageCount, setPage]);

    useEffect(() => {
        const itemKeys = new Set(items.map((item) => getItemKey(item)));
        setPickedValues((currentValues) => {
            const nextValues = currentValues.filter((value) => itemKeys.has(value));
            return arraysEqual(currentValues, nextValues) ? currentValues : nextValues;
        });
    }, [getItemKey, items, setPickedValues]);

    const pagedItems = useMemo(() => {
        const start = (page - 1) * rowsOnPage;
        return items.slice(start, start + rowsOnPage);
    }, [items, page, rowsOnPage]);

    const onHeaderCheckboxChange = useCallback(
        (checked: boolean) => {
            if (checked) {
                setPickedValues((currentValues) => {
                    const nextValues = new Set(currentValues);
                    for (const item of pagedItems) {
                        nextValues.add(getItemKey(item));
                    }

                    return [...nextValues];
                });
                return;
            }

            const currentPageKeys = new Set(pagedItems.map((item) => getItemKey(item)));
            setPickedValues((currentValues) => currentValues.filter((value) => !currentPageKeys.has(value)));
        },
        [getItemKey, pagedItems, setPickedValues],
    );

    const onCheckboxChange = useCallback(
        (checked: boolean, item: TItem) => {
            const itemKey = getItemKey(item);

            setPickedValues((currentValues) => {
                if (checked) {
                    if (currentValues.includes(itemKey)) {
                        return currentValues;
                    }

                    return [...currentValues, itemKey];
                }

                return currentValues.filter((value) => value !== itemKey);
            });
        },
        [getItemKey, setPickedValues],
    );

    const isHeaderChecked = useMemo(
        () => !!pagedItems.length && pagedItems.every((item) => pickedValuesSet.has(getItemKey(item))),
        [getItemKey, pagedItems, pickedValuesSet],
    );

    const checkboxData = useMemo<TableCheckboxData[]>(
        () =>
            pagedItems.map((item) => ({
                checked: pickedValuesSet.has(getItemKey(item)),
                disabled: false,
            })),
        [getItemKey, pagedItems, pickedValuesSet],
    );

    const onRowsOnPageUpdate = useCallback(
        (count: number) => {
            setRowsOnPage(count);
            setPage(1);
        },
        [setPage, setRowsOnPage],
    );

    return {
        totalRows,
        pageCount,
        pagedItems,
        isHeaderChecked,
        checkboxData,
        onHeaderCheckboxChange,
        onCheckboxChange,
        onRowsOnPageUpdate,
    };
};
